// src/pages/OrderService/OrderServiceModal.jsx
import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    MenuItem,
    Grid,
    Stack,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import {geraOrdemServico, fetchTiposServico, fetchUltimaAS} from '../../services/easyhubQueries';

export default function OrderServiceModal({open, onClose, onSuccess}) {
    const [form, setForm] = useState({bem: '', servico: '', hodometro: '', desc_obs: ''});
    const [asNum, setAsNum] = useState('');
    const [loadingAs, setLoadingAs] = useState(false);
    const [errorAs, setErrorAs] = useState('');

    // controle do fluxo de AS
    const [searchedAS, setSearchedAS] = useState(false); // já clicou em "buscar AS"?
    const [asNotFound, setAsNotFound] = useState(false); // não encontrou AS para o bem?
    const [asMsg, setAsMsg] = useState({type: '', text: ''}); // mensagem (info/warning/erro)

    const [tipos, setTipos] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTiposServico()
            .then(setTipos)
            .catch((err) => setError(err.message));
    }, []);

    const handleChange = ({target: {name, value}}) => {
        const upper = name === 'bem' || name === 'desc_obs' ? value.toUpperCase() : value;
        setForm((f) => ({...f, [name]: upper}));
        // se o usuário alterar o BEM, exige nova busca de AS
        if (name === 'bem') {
            setAsNum('');
            setSearchedAS(false);
            setAsNotFound(false);
            setAsMsg({type: '', text: ''});
        }
    };

    const handleFetchAS = async () => {
        if (!form.bem) {
            setAsMsg({type: 'error', text: 'Preencha o CÓDIGO do Bem antes'});
            return;
        }
        setLoadingAs(true);
        setAsMsg({type: '', text: ''});
        setSearchedAS(false);
        setAsNotFound(false);
        try {
            const as = await fetchUltimaAS(form.bem);
            const found = (as || '').trim();
            if (found) {
                setAsNum(found);
                setAsMsg({type: 'info', text: 'AS encontrada para o bem informado.'});
                setError('');
                setAsNotFound(false);
            } else {
                setAsNum('');
                setAsNotFound(true);
                setAsMsg({
                    type: 'warning',
                    text: 'AS não encontrada. É possível prosseguir com o campo em branco.',
                });
            }
        } catch (err) {
            // Não encontrado ou erro → permitir prosseguir em branco
            setAsNum('');
            setAsNotFound(true);
            setAsMsg({
                type: 'warning',
                text: 'AS não encontrada. É possível prosseguir com o campo em branco.',
            });
        } finally {
            setLoadingAs(false);
            setSearchedAS(true);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(''); // limpa mensagem antiga
        try {
            const resp = await geraOrdemServico(
                form.bem,
                form.servico,
                asNotFound ? '' : asNum,
                form.hodometro,
            );

            // Se vier um string numérica = OS gerada
            if (resp.Dados && typeof resp.Dados === 'string' && /^\d+$/.test(resp.Dados)) {
                // avisa o parent, persiste em localStorage, etc.
                onSuccess(resp.Dados, form.bem);

                // Mostra alerta de sucesso e fecha modal
                alert(`Ordem de Serviço ${resp.Dados} gerada com sucesso!`);
                onClose();
                return;
            }

            // Se veio string não-numérica = mensagem de validação
            if (resp.Dados && typeof resp.Dados === 'string') {
                setError(resp.Dados);
                return;
            }

            if (resp == '') {
                setError('Bem não encontrado ou inválido');
                return;
            }
            // Qualquer outro caso
            setError('Resposta inesperada do servidor');
        } catch (err) {
            // aqui entram erros de rede / HTTP
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // exige que o usuário clique em "buscar AS".
    // Se encontrou, usa a AS; se não encontrou, permite prosseguir em branco.
    const isValid =
        form.bem && form.servico && form.hodometro && searchedAS && (!!asNum || asNotFound);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <BuildIcon color="primary" />
                <Typography variant="h6" component="span">
                    Nova Ordem de Serviço
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {asMsg.text && <Alert severity={asMsg.type || 'info'}>{asMsg.text}</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="bem"
                                label="Bem / Veículo"
                                value={form.bem}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="AS (busca obrigatória)"
                                value={asNum}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleFetchAS}
                                                disabled={loadingAs}
                                            >
                                                {loadingAs ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <SearchIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                name="servico"
                                label="Tipo Serviço"
                                value={form.servico}
                                onChange={handleChange}
                                fullWidth
                            >
                                {tipos.map((t) => (
                                    <MenuItem key={t.code} value={t.code}>
                                        {t.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="hodometro"
                                label="Contador Km/Hr"
                                type="number"
                                value={form.hodometro}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="desc_obs"
                                label="Observações"
                                multiline
                                rows={4}
                                value={form.desc_obs}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || loading}
                    endIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
